import React, {useState, useCallback} from 'react';
import { Grid, FormControl, InputLabel, Select, MenuItem, makeStyles } from '@material-ui/core';
import ReportLayoutPage from './ReportLayoutPage';
import ReportFilter from './ReportFilter';
import Graph from './Graph';
import { useAttributePreference } from '../common/preferences';
import { formatDate } from '../common/formatter';
import { speedFromKnots } from '../common/converter';
import { t } from '../common/LocalizationProvider';
import {cyberStyles} from "../cssShared/cyberTheme";
import {useLocation} from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";

const useStyles = makeStyles((theme) => ({
  ...cyberStyles(theme),
  graphContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    backdropFilter: 'blur(12px)',
    borderRadius: '16px',
    border: '1px solid rgba(0, 242, 255, 0.15)',
    padding: theme.spacing(2),
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.6)',
  }
}));

const Filter = ({ children, setItems, loading, setLoading }) => {
  const speedUnit = useAttributePreference('speedUnit');
  const [openSnackbar, setOpenSnackbar] = useState(null)

  const handleSubmit = useCallback(  async (deviceIds, from, to, mail, headers) => {
    const query = new URLSearchParams({ from, to, mail });
    deviceIds.forEach(deviceId => query.append("deviceId", deviceId))

    if (deviceIds.length !== 0) {
      const response = await fetch(`/api/reports/routeghg?${query.toString()}`, {headers});
      if (response.ok) {
        const res = await response.json()
        setItems(res);
        if (res[0] === undefined || res[0]?.positions.length === 0) {
          setOpenSnackbar('replayNoData')
          return;
        }
        const positions = res[0]?.positions;
        let agtleg = res[0]?.agtleg || [];
        let gtileg = res[0]?.gtileg || [];
        let ghgEmission = res[0]?.ghgEmission || [];
        let blackCarbonGHG = res[0]?.blackCarbonEmission || [];

        var i = 0
        const roundEighthDigit = (number, digits) => {
          if (number === undefined)
            return;
          const roundedNumber = parseFloat(number.toFixed(digits));
          const multiplier = Math.pow(10, digits);
          const roundedWith8thDigit = Math.round(roundedNumber * multiplier) / multiplier;
          return roundedWith8thDigit;
        }

        let formattedPositions = positions?.map(position => {
          return {
            speed: Number(speedFromKnots(position.speed, speedUnit)),
            //altitude: position.altitude
            tanklevel: position.attributes.tanklevel,
            accuracy: position.accuracy,
            longitude: position.longitude,
            latitude: position.latitude,
            fuel_Level: position.attributes.fuelelevel,
            fuelpressure: position.attributes.fuelpressure,
            enginetemp: position.attributes.enginetemp,
            rpm: position.attributes.rpm,
            kph: position.attributes.kph,
            oiltemp: position.attributes.oiltemp,
            fuelrate: position.attributes.fuelrate,
            torque: position.attributes.torque,
            batteryvoltage: position.attributes.batteryvoltage,
            gpsSgn: position.attributes.gpsSgn,
            gsmSgn: position.attributes.gsmSgn,
            // fuel_volume:position.attributes.fuel_volume,
            fixTime: formatDate(position.fixTime),
            distance: position.attributes.distance,
            debug: position.attributes.debug,
            ghgemission: agtleg[i++],
            ghgtank_to_wheels: gtileg[i - 1],
            ghgEmission: roundEighthDigit(ghgEmission[i - 1], 8),
            blackCarbonEmission: roundEighthDigit(blackCarbonGHG[i - 1], 10)
          }
        });
        setItems(formattedPositions);
      }
      else
        setOpenSnackbar('UnknownProblem')
    }
    setLoading(null)
  },[setItems, speedUnit])

  return (
      <>
        <ReportFilter handleSubmit={handleSubmit} showOnly openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} loading={loading} setLoading={setLoading}/>
        {children}
      </>
  )
}

const ChartType = ({ types, setTypes }) => {
  const classes = useStyles();

  const handleChange = (event) => {
    setTypes(event.target.value);
  };

  return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl variant="filled" margin="normal" fullWidth className={classes.formControl}>
            <InputLabel>{t('reportChartType')}</InputLabel>
            <Select
                multiple
                value={types}
                onChange={handleChange}
                MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
            >
              <MenuItem className={classes.menuItem} value="speed">{t('positionSpeed')}</MenuItem>
              <MenuItem className={classes.menuItem} value="accuracy">{t('positionAccuracy')}</MenuItem>
              <MenuItem className={classes.menuItem} value="latitude">{t('positionLatitude')}</MenuItem>
              <MenuItem className={classes.menuItem} value="tanklevel">{t('positionTanklevel')}</MenuItem>
              <MenuItem className={classes.menuItem} value="longitude">{t('positionLongitude')}</MenuItem>
              <MenuItem className={classes.menuItem} value="fuel_Level">{t('positionFuelelevel')}</MenuItem>
              <MenuItem className={classes.menuItem} value="fuelpressure">{t('positionFuelpressure')}</MenuItem>
              <MenuItem className={classes.menuItem} value="enginetemp">{t( 'positionEnginetemp')}</MenuItem>
              <MenuItem className={classes.menuItem} value="rpm">{t('positionRpm')}</MenuItem>
              <MenuItem className={classes.menuItem} value="kph">{t('positionKph')}</MenuItem>
              <MenuItem className={classes.menuItem} value="oiltemp">{t('positionOiltemp')}</MenuItem>
              <MenuItem className={classes.menuItem} value="fuelrate">{t('positionFuelrate')}</MenuItem>
              <MenuItem className={classes.menuItem} value="torque">{t('positionTorque')}</MenuItem>
              <MenuItem className={classes.menuItem} value="batteryvoltage">{t('positionBatteryvoltage')}</MenuItem>
              <MenuItem className={classes.menuItem} value="gpsSgn">{t('gpsSgn')}</MenuItem>
              <MenuItem className={classes.menuItem} value="gsmSgn">{t('gsmSgn')}</MenuItem>
              {/*<MenuItem value="fuel_volume">{'Fuel Volume'}</MenuItem>*/}
              <MenuItem className={classes.menuItem} value="distance">{t('sharedDistance')}</MenuItem>
              <MenuItem className={classes.menuItem} value="debug">{t( 'debug')}</MenuItem>
              {/*<MenuItem value="ghgemission">{t('reportGHGEmission')}</MenuItem>*/}
              {/*<MenuItem value="ghgtank_to_wheels">{t('reportGHGtank_to_wheels')}</MenuItem>*/}
              <MenuItem className={classes.menuItem} value="ghgEmission">{t('GHG_Emission')}</MenuItem>
              <MenuItem className={classes.menuItem} value="blackCarbonEmission">{t('blackCarbonGHG')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
  )
}

const ChartReportPage = () => {
  const classes = useStyles();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const typeParam = queryParams.get('type');

  const [items, setItems] = useState([]);
  const [types, setTypes] = useState(typeParam ? [typeParam] : ['speed']);
  const [loading, setLoading] = useState(null);

  return (
      <ReportLayoutPage filter={
        <Filter setItems={setItems} loading={loading} setLoading={setLoading}>
          <ChartType types={types} setTypes={setTypes} />
        </Filter>
      }>
        <div className={classes.graphContainer}>
            {loading ? (
                <div className={classes.centerLoader}>
                    <CircularProgress color="secondary" size={40} />
                </div>
                ) :
                <Graph items={items} types={types} />
            }
        </div>
      </ReportLayoutPage>
  )
}

export default ChartReportPage;